import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Radio, ArrowRight, BarChart3, Cloud, MessagesSquare } from "lucide-react";

export default function Landing() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const join = async (e) => {
    e.preventDefault();
    const c = code.trim();
    if (c.length < 4) {
      toast.error("Enter the code shown on screen");
      return;
    }
    setLoading(true);
    try {
      await api.get(`/join/${c}`);
      navigate(`/j/${c}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "No active session with that code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden flex flex-col">
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-indigo-600/30 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-violet-600/20 blur-3xl" />

      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="flex items-center gap-2 font-display font-extrabold text-xl">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Radio className="w-5 h-5" />
          </span>
          Pulse
        </div>
        <button
          data-testid="presenter-login-link"
          onClick={() => navigate("/login")}
          className="text-sm font-semibold text-slate-300 hover:text-white transition-colors"
        >
          Presenter login →
        </button>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pb-20">
        <div className="text-center max-w-2xl">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-tight">
            Join a live session
          </h1>
          <p className="mt-4 text-slate-400 text-base sm:text-lg">
            Enter the code from the presenter's screen to vote, share words and ask questions in real time.
          </p>

          <form onSubmit={join} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              data-testid="audience-join-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="123456"
              className="flex-1 text-center sm:text-left font-mono-code text-2xl font-extrabold tracking-widest px-5 py-4 rounded-2xl bg-white/10 border border-white/20 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/20 outline-none placeholder:text-slate-500"
            />
            <button
              data-testid="audience-join-submit-button"
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-violet-600 hover:opacity-95 font-bold rounded-2xl px-7 py-4 shadow-lg shadow-indigo-900/40 transition-all disabled:opacity-60"
            >
              {loading ? "Joining…" : "Join"} <ArrowRight className="w-5 h-5" />
            </button>
          </form>

          <div className="mt-14 flex items-center justify-center gap-8 text-slate-500">
            <div className="flex flex-col items-center gap-2"><BarChart3 className="w-6 h-6" /><span className="text-xs">Live polls</span></div>
            <div className="flex flex-col items-center gap-2"><Cloud className="w-6 h-6" /><span className="text-xs">Word clouds</span></div>
            <div className="flex flex-col items-center gap-2"><MessagesSquare className="w-6 h-6" /><span className="text-xs">Q&amp;A</span></div>
          </div>
        </div>
      </main>
    </div>
  );
}
